'use client';

// ExternalLinkDialog - Confirmation dialog before navigating to external URLs
// Shows a disclaimer that Boty is not responsible for external content

import { useCallback, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslations } from 'next-intl';

export interface ExternalLinkDialogState {
  open: boolean;
  url: string;
}

const initialState: ExternalLinkDialogState = { open: false, url: '' };

/**
 * Hook to manage external link dialog state
 */
export function useExternalLinkDialog() {
  const [state, setState] = useState<ExternalLinkDialogState>(initialState);

  const openDialog = useCallback((url: string) => {
    setState({ open: true, url });
  }, []);

  const closeDialog = useCallback(() => {
    setState(initialState);
  }, []);

  return { state, openDialog, closeDialog };
}

export interface ExternalLinkDialogProps {
  open: boolean;
  url: string;
  onClose: () => void;
}

export function ExternalLinkDialog({ open, url, onClose }: ExternalLinkDialogProps) {
  const t = useTranslations('consumer.chat.externalLink');

  const handleContinue = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <OpenInNewIcon fontSize="small" />
        {t('title')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('message')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('url')}
        </Typography>
        <Box
          sx={{
            mt: 0.5,
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            wordBreak: 'break-all',
          }}
        >
          <Typography variant="body2" dir="ltr">
            {url}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('cancel')}
        </Button>
        <Button onClick={handleContinue} variant="contained">
          {t('continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
